import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ClarityModule } from '@clr/angular';
import { finalize, Subscription } from 'rxjs';
import { Broadcast } from '../../../../models/broadcast';
import { environment } from '../../../../../environments/environment';
import { VoxtralCorrectionContextEditorComponent } from '../voxtral-correction-context-editor/voxtral-correction-context-editor.component';
import { OverlayPanelComponent } from '../../../../shared/components/overlay-panel/overlay-panel.component';
import { defaultFabUrlForChannel } from '../voxtral-fab-defaults';
import { BroadcastsService } from '../../services/broadcasts.service';
import { AuthorizationService } from '../../../../../security/authorization.service';
import { DisableUnlessPermissionDirective } from '../../../../../security/disable-unless-permission.directive';

// 2026-08 — LLM correction modes (llm / hybrid / fast_hybrid) removed with
// the llama-corrector stack. Remaining engines: Rust phonetic sidecar
// (default) and the in-process fuzzy corrector.
export type CorrectionMode = 'off' | 'fuzzy' | 'phonetic';

/**
 * 2026-06-21 — return the planned start of a broadcast (StartTime, falling
 * back to DdsStartsAt only if StartTime is missing) as a UTC-milliseconds
 * timestamp, or null if both fields are absent/unparseable.
 *
 * Used by the Programmtag window filter so a broadcast that the DDS feed
 * reports as having started late (DdsStartsAt past the 06:00-next-day
 * boundary) still appears in its scheduled Programmtag. The display path
 * keeps the existing "DdsStartsAt first" semantics so an aired-late
 * broadcast still shows its actual airtime in the row.
 *
 * Exported for unit tests in voxtral-schedule-dialog.component.spec.ts.
 */
export function scheduledStartMs(b: Pick<Broadcast, 'startTime' | 'ddsStartsAt'> | null | undefined): number | null {
  if (!b) return null;
  const raw = b.startTime || b.ddsStartsAt;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return isNaN(t) ? null : t;
}

export interface VoxtralScheduleRow {
  key: string;
  channel: string;
  title: string | null;
  subtitle: string | null;
  startsAt: Date;
  /**
   * 2026-06-21 — planned start derived from StartTime (= VpsCode local).
   * Used for Programmtag window filter + sort. Distinct from startsAt
   * which honours DdsStartsAt for display "actual airtime" semantics.
   */
  scheduledStartsAt: Date;
  endsAt: Date;
  scheduled: boolean;
  sessionActive: boolean;
  sessionStarting: boolean;
  ec2Starting: boolean;
  ec2Ip: string | null;
  sessionId: string | null;
  operatorStoppedAt: string | null;
  finished: boolean;
  past: boolean;
  showPastBadge: boolean;
  current: boolean;
  // Inline-edit fields — mirror what details.component.ts exposes per broadcast.
  fabEnabled: boolean;
  // 2026-06-08 — mid-broadcast FAB-URL editor in the table row. Empty
  // string falls back to defaultFabUrlForChannel at start time.
  fabUrl: string;
  correctionMode: CorrectionMode;
  correctionContext: string;
  ec2StartedAt: string | null;
  sessionStartedAt: string | null;
  // Phase H2 (2026-05-28) — operator opt-out for the post-broadcast docker
  // container restart. Defaults to `true` (matches the env default in
  // voxtral-server). When unchecked, voxtral-server keeps the existing
  // container alive at session-stop instead of self-restarting.
  restartAfterBroadcast: boolean;
  // 2026-07-02 — per-broadcast DDS-interval FAB gate toggle. Defaults to
  // true (checked). Unchecked = gate bypassed (FAB gets every frame).
  isFabDdsStartStopActive: boolean;
  // 2026-07-02 — accumulated Programm-segment DDS intervals on the row.
  // Read-only summary badge under the checkbox. Empty = "no intervals
  // yet" (voxtral falls back to single-window).
  ddsIntervals: Array<{ startsAt: string; endsAt: string }>;
}

@Component({
  selector: 'app-voxtral-schedule-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, VoxtralCorrectionContextEditorComponent, OverlayPanelComponent, DisableUnlessPermissionDirective],
  templateUrl: './voxtral-schedule-dialog.component.html',
  styleUrl: './voxtral-schedule-dialog.component.scss',
})
export class VoxtralScheduleDialogComponent implements OnInit, OnDestroy {
  @Input() date!: Date;
  @Input() channel!: string;
  @Input() broadcasts: Broadcast[] = [];
  @Output() closed = new EventEmitter<void>();

  // RBAC — Start/Stop sind Admin-only; alles andere sperrt das Fieldset
  // um .sched-cards für Editor (sichtbar, aber deaktiviert).
  private readonly authz = inject(AuthorizationService);
  get canControlVoxtral(): boolean { return this.authz.hasPermission('control-voxtral'); }

  errorMessage = '';
  toggleInFlight = new Set<string>();
  fabSavingKeys = new Set<string>();
  // 2026-06-08 — mid-broadcast FAB-URL editor (per-row save state + debounce
  // handles keyed by broadcast key, mirroring fabSavingKeys for the toggle).
  fabUrlSavingKeys = new Set<string>();
  private fabUrlDebounceHandles = new Map<string, ReturnType<typeof setTimeout>>();
  correctionSavingKeys = new Set<string>();
  startInFlight = new Set<string>();
  stopInFlight = new Set<string>();
  // Per-row context-editor expansion state. A row is in the set if its
  // Korrektur-Kontext drawer is open.
  expandedContextKeys = new Set<string>();
  // Per-row last validated context length (kept separately so the table can
  // show a "N Zeichen" hint without re-measuring on every CD cycle).
  readonly correctionContextPlaceholder = '{"type":"terms","terms":["..."]}';
  readonly correctionContextHint = 'JSON-Format: {"type":"terms","terms":[...]} oder {"type":"teams","teams":[...]} — wird beim nächsten Start angewendet.';

  // Periodic tick to keep `past` / `current` row flags fresh without relying
  // solely on Apollo broadcast updates, which can be quiet for minutes.
  private timeTick: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private broadcastsService: BroadcastsService,
  ) {}

  // 2026-06-08 — dialog-owned 24-hour broadcast cache. The parent
  // (programplan) only loads ONE calendar day; operators scheduling
  // Voxtral often need to see late-night / next-morning broadcasts that
  // fall into the same broadcasting-day (e.g. Formel-1 Monaco at
  // 05:05 next-day belongs to today's broadcasting day, not the
  // calendar next-day).
  //
  // Programmtag convention: 06:00 selectedDate (local) →
  // 05:59 selectedDate+1 (local). We fetch the parent's day AND the
  // following calendar day, then filter to that 24h LOCAL window.
  // Two Apollo queries — minimal load, covers the cross-midnight tail.
  /** Programmtag starts at this local-hour-of-day. */
  readonly broadcastDayStartHour = 6;
  private fetchedBroadcasts: Broadcast[] = [];
  private fetchSubs: Subscription[] = [];

  ngOnInit(): void {
    this.timeTick = setInterval(() => this.cdr.markForCheck(), 30_000);
    this._fetchBroadcastingDay();
  }

  /**
   * Issue one Apollo query for `selectedDate` and one for
   * `selectedDate + 1` so the cross-midnight tail (00:00–05:59 next
   * calendar day) is in the cache before `_computeRows` runs. Each
   * batch merges into `fetchedBroadcasts` (de-dup by key) and triggers
   * a row recompute.
   */
  private _fetchBroadcastingDay(): void {
    if (!this.date || !this.channel) return;
    const base = new Date(this.date.getTime());
    base.setUTCHours(0, 0, 0, 0);
    for (const offset of [0, 1]) {
      const d = new Date(base.getTime());
      d.setUTCDate(d.getUTCDate() + offset);
      const sub = this.broadcastsService.getData('LIVE', d, this.channel).subscribe({
        next: (result: any) => {
          const arr: Broadcast[] = (result?.data?.broadcasts || []) as Broadcast[];
          if (!arr.length) return;
          const seen = new Set<string>(this.fetchedBroadcasts.map((b) => b.key));
          for (const b of arr) {
            if (b && b.key && !seen.has(b.key)) {
              this.fetchedBroadcasts.push(b);
              seen.add(b.key);
            }
          }
          // Force row recompute on the next change-detection pass.
          this._rowsCache = [];
          this._rowsSig = null;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          // Non-fatal — the parent-supplied `broadcasts` still works for today.
          console.warn('[voxtral-schedule-dialog] broadcasting-day fetch failed for', d, err);
        },
      });
      this.fetchSubs.push(sub);
    }
  }

  ngOnDestroy(): void {
    if (this.timeTick) {
      clearInterval(this.timeTick);
      this.timeTick = null;
    }
    // 2026-06-08 — release the lookahead Apollo subscriptions so they
    // don't keep firing after the dialog closes.
    for (const sub of this.fetchSubs) {
      try { sub.unsubscribe(); } catch { /* ignore */ }
    }
    this.fetchSubs = [];
    this.fetchedBroadcasts = [];
  }

  // §6 perf — memoized rows.
  // `toRow()` does ISO-duration regex parsing, several `new Date()` calls and
  // an 18-field object allocation PER broadcast. The previous getter ran that
  // for every broadcast on EVERY change-detection pass — and CD fires whenever
  // the operator merely opens a <select> or toggles a checkbox. On a busy
  // day (50-200 broadcasts) that froze the dialog.
  // We now recompute only when a cheap content+time signature changes.
  private _rowsCache: VoxtralScheduleRow[] = [];
  private _rowsSig: string | null = null; // sentinel — never equals a real signature

  /**
   * Rows still reflect live Apollo pushes into the parent's `broadcasts`
   * array — the signature below covers every field `toRow()` reads, so any
   * subscription update recomputes; a pure dropdown/checkbox CD pass does not.
   */
  get rows(): VoxtralScheduleRow[] {
    const sig = this._rowsSignature();
    if (sig !== this._rowsSig) {
      this._rowsSig = sig;
      this._rowsCache = this._computeRows();
    }
    return this._rowsCache;
  }

  /**
   * Cheap O(n) string scan of every field `toRow()` consumes, plus a 30 s time
   * bucket so the time-derived `past`/`current` flags still refresh on the
   * periodic tick. Building this is far cheaper than running `toRow()` for
   * every broadcast — no Date construction, no regex, no allocations.
   */
  private _rowsSignature(): string {
    if (!this.channel || !this.date || !this.broadcasts) return '';
    const bucket = Math.floor(Date.now() / 30_000);
    const parts: string[] = [String(bucket), this.channel, this.formatDate(this.date)];
    for (const b of this.broadcasts) {
      if (!b || b.channel !== this.channel) continue;
      parts.push(
        b.key,
        b.title || '',
        b.subtitle || '',
        b.ddsStartsAt || b.startTime || '',
        b.ddsEndsAt || '',
        b.bruttoDuration || '',
        b.voxtralMini4bRealtimeEc2IP || '',
        b.voxtralMini4bRealtimeSessionId || '',
        b.voxtralMini4bRealtimeSessionStoppedAt || '',
        b.voxtralMini4bRealtimeEc2StoppedAt || '',
        b.voxtralMini4bRealtimeOperatorStoppedAt || '',
        (b.voxtralMini4bRealtimeCorrectionMode as string) || '',
        b.voxtralMini4bRealtimeCorrectionEnabled ? '1' : '0',
        b.voxtralMini4bRealtimeScheduled ? '1' : '0',
        b.voxtralMini4bRealtimeFabEnabled ? '1' : '0',
        b.voxtralMini4bRealtimeCorrectionContext || '',
        b.voxtralMini4bRealtimeEc2StartedAt || '',
        b.voxtralMini4bRealtimeSessionStartedAt || '',
        // Phase H2 (2026-05-28) - restart-flag in the signature so Apollo pushes
        // refresh the checkbox state without a full rebuild.
        (((b as any).voxtralMini4bRealtimeRestartAfterBroadcast ?? '')).toString(),
        // 2026-07-02 — DDS-interval FAB gate toggle + intervals length in
        // the signature so both toggle changes AND new DDS notifications
        // trigger a row re-render.
        (((b as any).voxtralMini4bRealtimeIsFabDdsStartStopActive ?? '')).toString(),
        (Array.isArray((b as any).ddsIntervals) ? (b as any).ddsIntervals.length : 0).toString(),
      );
    }
    return parts.join('');
  }

  private _computeRows(): VoxtralScheduleRow[] {
    if (!this.channel || !this.date) return [];
    // 2026-06-08 — merge parent-supplied broadcasts with the dialog's
    // broadcasting-day fetch + de-dup by key. Filter to channel +
    // [selectedDate 06:00 LOCAL, selectedDate+1 06:00 LOCAL) — the ORF
    // broadcasting-day convention (06:00 → 05:59 next-day) keeps
    // late-night and next-morning rows on the same day's view.
    const seen = new Set<string>();
    const merged: Broadcast[] = [];
    const collect = (arr: Broadcast[] | undefined) => {
      if (!arr) return;
      for (const b of arr) {
        if (!b || !b.key) continue;
        if (seen.has(b.key)) continue;
        seen.add(b.key);
        merged.push(b);
      }
    };
    collect(this.broadcasts);
    collect(this.fetchedBroadcasts);

    // selectedDate is UTC-midnight of the calendar day the operator
    // picked. Set local hours to broadcastDayStartHour to anchor the
    // start at 06:00 local; .setDate(+1) gives the natural 24-hour end
    // anchored at the same local hour, DST changes included.
    const start = new Date(this.date.getTime());
    start.setHours(this.broadcastDayStartHour, 0, 0, 0);
    const end = new Date(start.getTime());
    end.setDate(end.getDate() + 1);
    const startMs = start.getTime();
    const endMs = end.getTime();

    // 2026-06-21 — Programmtag bucketing uses the PLANNED start
    // (StartTime / VpsCode), NOT DdsStartsAt. A late-starting broadcast
    // (e.g. FIFA at planned 05:55 but DDS-actual 07:02) still belongs to
    // its scheduled Programmtag. Using DdsStartsAt here would push it past
    // the 06:00-next-day boundary and drop the row entirely. See memory
    // project_programmtag_calendar_day_merge.
    return merged
      .filter((b) => b && b.channel === this.channel)
      .filter((b) => {
        const sched = scheduledStartMs(b);
        return sched != null && sched >= startMs && sched < endMs;
      })
      .map((b) => this.toRow(b))
      .sort((a, b) => {
        const aSched = a.scheduledStartsAt.getTime();
        const bSched = b.scheduledStartsAt.getTime();
        return aSched - bSched;
      });
  }

  private toRow(b: Broadcast): VoxtralScheduleRow {
    const startsAt = this.resolveStart(b);
    const scheduledStartsAt = this.resolveScheduledStart(b);
    const endsAt = this.resolveEnd(b, startsAt);
    const ec2Ip = b.voxtralMini4bRealtimeEc2IP;
    const sessionId = b.voxtralMini4bRealtimeSessionId;
    const ec2Starting = ec2Ip === 'blocked';
    const sessionStarting =
      !!ec2Ip &&
      ec2Ip !== 'blocked' &&
      !sessionId &&
      !b.voxtralMini4bRealtimeSessionStoppedAt;
    const sessionActive = !!ec2Ip && ec2Ip !== 'blocked' && !!sessionId;
    const finished = !ec2Ip && !!b.voxtralMini4bRealtimeEc2StoppedAt;
    const now = Date.now();
    const past = !isNaN(endsAt.getTime()) && endsAt.getTime() < now;
    const current = !isNaN(startsAt.getTime()) && !isNaN(endsAt.getTime())
      && startsAt.getTime() <= now && now <= endsAt.getTime();
    const operatorStoppedAt = b.voxtralMini4bRealtimeOperatorStoppedAt || null;
    const showPastBadge = past
      && !finished
      && !ec2Starting
      && !sessionStarting
      && !sessionActive
      && !operatorStoppedAt;
    const persistedMode = b.voxtralMini4bRealtimeCorrectionMode as string | null | undefined;
    let correctionMode: CorrectionMode;
    if (
      persistedMode === 'off' ||
      persistedMode === 'fuzzy' ||
      persistedMode === 'phonetic'
    ) {
      correctionMode = persistedMode;
    } else if (
      persistedMode === 'llm' ||
      persistedMode === 'hybrid' ||
      persistedMode === 'fast_hybrid'
    ) {
      // Legacy LLM modes (correctors removed 2026-08) — fuzzy is the closest
      // surviving behaviour; the backend coerces the same way when it
      // forwards the persisted row to voxtral.
      correctionMode = 'fuzzy';
    } else {
      // Nothing persisted yet: Phonetisch, which is what the backend forwards
      // for a row without a mode (CorrectionModeCompat, 2026-10-13).
      correctionMode = 'phonetic';
    }
    return {
      key: b.key,
      channel: b.channel,
      title: b.title || null,
      subtitle: b.subtitle || null,
      startsAt,
      scheduledStartsAt,
      endsAt,
      scheduled: !!b.voxtralMini4bRealtimeScheduled,
      sessionActive,
      sessionStarting,
      ec2Starting,
      ec2Ip: ec2Ip && ec2Ip !== 'blocked' ? ec2Ip : null,
      sessionId: sessionId || null,
      operatorStoppedAt,
      finished,
      past,
      showPastBadge,
      current,
      fabEnabled: !!b.voxtralMini4bRealtimeFabEnabled,
      fabUrl: b.voxtralMini4bRealtimeFabUrl || '',
      correctionMode,
      correctionContext: b.voxtralMini4bRealtimeCorrectionContext || '',
      ec2StartedAt: b.voxtralMini4bRealtimeEc2StartedAt || null,
      sessionStartedAt: b.voxtralMini4bRealtimeSessionStartedAt || null,
      // Phase H2 — default checked when the broadcast row has no explicit
      // value yet (matches the env-level default of `true`). Operators
      // can uncheck to opt out per-broadcast.
      restartAfterBroadcast: b.voxtralMini4bRealtimeRestartAfterBroadcast === false ? false : true,
      // 2026-07-02 — default checked (matches backend default); only false
      // when the row explicitly says so. Both this flag and
      // restartAfterBroadcast above joined the GraphQL selection set on
      // 2026-09-02 — before that they were always `undefined` here, so an
      // operator's untick came back ticked on the next dialog open.
      isFabDdsStartStopActive:
        b.voxtralMini4bRealtimeIsFabDdsStartStopActive === false ? false : true,
      ddsIntervals: this._normalizeDdsIntervals(b.ddsIntervals),
    };
  }

  /**
   * 2026-07-02 — coerce the accumulated DDS intervals into the frontend
   * row shape (defensive: DDB may hand us any of null / undefined / an
   * array of {StartsAt, EndsAt} PascalCase / camelCase).
   */
  private _normalizeDdsIntervals(raw: any): Array<{ startsAt: string; endsAt: string }> {
    if (!Array.isArray(raw)) return [];
    const out: Array<{ startsAt: string; endsAt: string }> = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const s = item.startsAt || item.StartsAt || '';
      const e = item.endsAt || item.EndsAt || '';
      if (typeof s === 'string' && typeof e === 'string' && (s || e)) {
        out.push({ startsAt: s, endsAt: e });
      }
    }
    return out;
  }

  private resolveStart(b: Broadcast): Date {
    const raw = b.ddsStartsAt || b.startTime;
    return raw ? new Date(raw) : new Date(NaN);
  }

  /**
   * Planned start of the broadcast (used for Programmtag bucketing). Always
   * prefer the scheduled StartTime over DdsStartsAt so a late-starting
   * broadcast still appears in its scheduled Programmtag rather than
   * silently disappearing because DdsStartsAt has drifted past the
   * 06:00-next-day boundary. See project_programmtag memory.
   */
  private resolveScheduledStart(b: Broadcast): Date {
    const raw = b.startTime || b.ddsStartsAt;
    return raw ? new Date(raw) : new Date(NaN);
  }

  private resolveEnd(b: Broadcast, startsAt: Date): Date {
    if (b.ddsEndsAt) return new Date(b.ddsEndsAt);
    const minutes = this.isoDurationToMinutes(b.bruttoDuration || 'PT0H0M');
    return new Date(startsAt.getTime() + minutes * 60_000);
  }

  private isoDurationToMinutes(duration: string): number {
    if (!duration) return 0;
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;
    const h = parseInt(match[1] || '0', 10);
    const m = parseInt(match[2] || '0', 10);
    const s = parseInt(match[3] || '0', 10);
    return h * 60 + m + s / 60;
  }

  private formatDate(d: Date): string {
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  toggle(row: VoxtralScheduleRow): void {
    if (this.toggleInFlight.has(row.key)) return;
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;

    const previous = !!broadcast.voxtralMini4bRealtimeScheduled;
    const target = !previous;
    broadcast.voxtralMini4bRealtimeScheduled = target;
    if (target) {
      // Server-side ScheduleAsync clears the operator-stop veto; mirror that
      // optimistically so the UI doesn't keep showing "manuell gestoppt".
      broadcast.voxtralMini4bRealtimeOperatorStoppedAt = undefined;
    }
    this.toggleInFlight.add(row.key);
    this.errorMessage = '';

    const url = environment.API_Base + '/voxtral-schedule/' + encodeURIComponent(row.key);
    const request$ = target ? this.http.post(url, {}) : this.http.delete(url);

    request$
      .pipe(finalize(() => this.toggleInFlight.delete(row.key)))
      .subscribe({
        next: () => { /* Apollo subscription pushes canonical state. */ },
        error: (err: any) => {
          console.error('VoxtralScheduleDialog: toggle failed', err);
          broadcast.voxtralMini4bRealtimeScheduled = previous;
          this.errorMessage =
            err?.error?.message || err?.message || 'Planung konnte nicht aktualisiert werden.';
        },
      });
  }

  isFabUrlSaving(row: VoxtralScheduleRow): boolean {
    return this.fabUrlSavingKeys.has(row.key);
  }

  /**
   * 2026-06-08 — persist FAB-URL change for this row AND live-update the
   * running voxtral session. Debounced so a keystroke storm doesn't fan
   * out into a request per character.
   */
  onFabUrlChanged(row: VoxtralScheduleRow, value: string): void {
    const trimmed = (value ?? '').trim();
    const handle = this.fabUrlDebounceHandles.get(row.key);
    if (handle) clearTimeout(handle);
    if (!trimmed) return;
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    this.fabUrlDebounceHandles.set(
      row.key,
      setTimeout(() => this._persistFabUrl(row, broadcast, trimmed), 600),
    );
  }

  private _persistFabUrl(
    row: VoxtralScheduleRow,
    broadcast: Broadcast,
    fabUrl: string,
    force = false,
  ): void {
    if (this.fabUrlSavingKeys.has(row.key)) return;
    const previous = (broadcast.voxtralMini4bRealtimeFabUrl as string) || '';
    // 2026-06-09 — `force` bypasses the idempotency check so the FAB
    // toggle handler can re-assert the channel default (or the row's
    // current URL) on every on/off click, even when local state already
    // matches. The keystroke-debounce path stays idempotent (force=false).
    if (!force && fabUrl === previous) return;
    broadcast.voxtralMini4bRealtimeFabUrl = fabUrl;
    row.fabUrl = fabUrl;
    this.fabUrlSavingKeys.add(row.key);
    this.errorMessage = '';

    this.http
      .post<any>(environment.API_Base + '/broadcast/voxtral-fab-url', {
        broadcastKey: row.key,
        fabUrl,
      })
      .pipe(finalize(() => this.fabUrlSavingKeys.delete(row.key)))
      .subscribe({
        next: (resp: any) => {
          if (resp?.live === false && row.sessionActive) {
            this.errorMessage = `[${this.shortTitle(row)}] FAB-URL Live-Update fehlgeschlagen — wird beim nächsten Start angewendet.`;
          }
        },
        error: (err: any) => {
          broadcast.voxtralMini4bRealtimeFabUrl = previous;
          row.fabUrl = previous;
          this.errorMessage =
            err?.error?.message || err?.message || 'FAB-URL konnte nicht aktualisiert werden.';
        },
      });
  }

  /**
   * Persist FAB enable on the broadcast row AND live-update the running
   * Voxtral session if one exists. Mirrors details.component.ts but scoped
   * to a row inside the table. Optimistic; reverts on error.
   */
  onFabToggle(row: VoxtralScheduleRow, value: boolean): void {
    if (this.fabSavingKeys.has(row.key)) return;
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    const previous = !!broadcast.voxtralMini4bRealtimeFabEnabled;
    if (previous === value) return;
    broadcast.voxtralMini4bRealtimeFabEnabled = value;
    this.fabSavingKeys.add(row.key);
    this.errorMessage = '';

    // 2026-06-09 — re-assert the FAB URL on EVERY toggle (on AND off).
    // Operators rely on this to "lock in" the channel-correct URL when
    // toggling, including the case where a row already has a stale URL
    // persisted (e.g. PRD ORF1 holding the legacy 10.128.16.38 instead
    // of the channel-correct 10.128.16.34). Behaviour:
    //   • empty persisted URL → write the channel default
    //   • non-empty persisted URL → re-write the same value (idempotent
    //     on the server, but force=true defeats the local short-circuit
    //     so a stale-but-equal-to-local value still hits the wire and
    //     keeps state in sync after a server-side fix-up).
    // Fire-and-forget — a transient URL failure does NOT roll back the
    // enable toggle the operator just clicked.
    const existing = (broadcast.voxtralMini4bRealtimeFabUrl as string || '').trim();
    const target = existing || defaultFabUrlForChannel(broadcast.channel);
    if (target) {
      this._persistFabUrl(row, broadcast, target, /* force */ true);
    }

    this.http
      .post<any>(environment.API_Base + '/broadcast/voxtral-fab-enabled', {
        broadcastKey: row.key,
        fabEnabled: value,
      })
      .pipe(finalize(() => this.fabSavingKeys.delete(row.key)))
      .subscribe({
        next: (resp: any) => {
          if (resp?.live === false && row.sessionActive) {
            this.errorMessage = `[${this.shortTitle(row)}] FAB-Live-Update fehlgeschlagen — wird beim nächsten Start angewendet.`;
          }
        },
        error: (err: any) => {
          broadcast.voxtralMini4bRealtimeFabEnabled = previous;
          this.errorMessage =
            err?.error?.message || err?.message || 'FAB konnte nicht aktualisiert werden.';
        },
      });
  }

  /**
   * Channel-aware FAB URL placeholder/fallback for the table row.
   * Used by the template so the inline editor shows a meaningful hint
   * (per-env defaults from voxtral-fab-defaults) when the row has no
   * persisted URL, instead of a hard-coded 10.128.16.38 literal.
   */
  defaultFabUrlForRow(row: VoxtralScheduleRow): string {
    return defaultFabUrlForChannel(row.channel);
  }

  /**
   * Phase H2 (2026-05-28) — persist the per-broadcast opt-out for the
   * post-broadcast docker container restart. There is NO live-update path:
   * the flag is consumed by voxtral-server at session-stop time, and any
   * change after the broadcast started reaches the running session via the
   * next DDB read anyway (Lambda starter / VoxtralRealtimeService rebuild
   * the session_config from the row, which we just patched).
   */
  onRestartAfterBroadcastChange(row: VoxtralScheduleRow, value: boolean): void {
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    // See onIsFabDdsStartStopActiveChanged — same row-lag trap.
    const previous = row.restartAfterBroadcast;
    if (previous === value) return;
    // Optimistic update on both row + broadcast so the checkbox state and
    // signature reflect the operator click immediately.
    row.restartAfterBroadcast = value;
    (broadcast as any).voxtralMini4bRealtimeRestartAfterBroadcast = value;
    this.errorMessage = '';

    this.http
      .post<any>(environment.API_Base + '/broadcast/voxtral-restart-after-broadcast', {
        broadcastKey: row.key,
        restartAfterBroadcast: value,
      })
      .subscribe({
        next: () => {
          // No-op on success — the persisted boolean already matches the
          // optimistic state we wrote above.
        },
        error: (err: any) => {
          // Roll back optimistic write on failure so the UI doesn't lie.
          row.restartAfterBroadcast = previous;
          (broadcast as any).voxtralMini4bRealtimeRestartAfterBroadcast = previous;
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Restart-Flag konnte nicht aktualisiert werden.';
        },
      });
  }

  /**
   * 2026-07-02 — per-broadcast operator toggle for the DDS-interval FAB
   * gate. Persist on the row AND live-push to the running session (the
   * backend endpoint fires UpdateBroadcastWindowAsync so voxtral picks
   * up the flag change on the next FAB tick). Same optimistic-with-
   * rollback pattern as onRestartAfterBroadcastChange.
   */
  onIsFabDdsStartStopActiveChanged(row: VoxtralScheduleRow, value: boolean): void {
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    // 2026-09-02 — `previous` is what the checkbox was rendering, not what the
    // broadcast row says. The row lags this POST by a round-trip and, until the
    // GraphQL selection set was fixed, never carried the field at all — which
    // collapsed "tick it back on" into a silent no-op.
    const previous = row.isFabDdsStartStopActive;
    if (previous === value) return;
    row.isFabDdsStartStopActive = value;
    (broadcast as any).voxtralMini4bRealtimeIsFabDdsStartStopActive = value;
    this.errorMessage = '';

    this.http
      .post<any>(environment.API_Base + '/broadcast/voxtral-fab-dds-start-stop-active', {
        broadcastKey: row.key,
        isFabDdsStartStopActive: value,
      })
      .subscribe({
        next: () => { /* optimistic write already matches persisted state */ },
        error: (err: any) => {
          row.isFabDdsStartStopActive = previous;
          (broadcast as any).voxtralMini4bRealtimeIsFabDdsStartStopActive = previous;
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'DDS-FAB-Gate konnte nicht aktualisiert werden.';
        },
      });
  }

  /**
   * 2026-07-02 — helper for the read-only summary badge under the checkbox.
   * Formats each accumulated DDS interval as "HH:MM-HH:MM" (Vienna local
   * time) so the operator can see which Programm segments the FAB gate
   * will follow. Returns "" when the list is empty.
   */
  formatDdsIntervals(intervals: Array<{ startsAt: string; endsAt: string }> | undefined | null): string {
    if (!intervals || intervals.length === 0) return '';
    return intervals
      .map((iv) => {
        const s = this._formatViennaHm(iv.startsAt);
        const e = this._formatViennaHm(iv.endsAt);
        return `${s}-${e}`;
      })
      .join(', ');
  }

  private _formatViennaHm(iso: string): string {
    if (!iso) return '??';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '??';
    // Local browser time — matches how startsAt / endsAt are displayed
    // elsewhere in the dialog. Operators run in Vienna TZ.
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /**
   * Persist correction mode on the broadcast row AND live-update the running
   * Voxtral session if one exists. Same DDB-first / live-second semantics as
   * details.component.ts.
   */
  onCorrectionModeChange(row: VoxtralScheduleRow, target: CorrectionMode): void {
    if (row.correctionMode === target) return;
    this._postMode(row, target);
  }

  /**
   * Single source of truth for /broadcast/voxtral-set-correction-mode POSTs
   * from this dialog. Optimistic with rollback; surfaces a live=false hint
   * when the running session could not be updated in place.
   */
  private _postMode(row: VoxtralScheduleRow, target: CorrectionMode): void {
    if (this.correctionSavingKeys.has(row.key)) return;
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;

    // Capture rollback state BEFORE optimistic update.
    const previousMode = row.correctionMode;
    const previousEnabled = !!broadcast.voxtralMini4bRealtimeCorrectionEnabled;

    // Optimistic mode update on the broadcast object so downstream UI sees it
    // immediately.
    broadcast.voxtralMini4bRealtimeCorrectionMode = target;
    broadcast.voxtralMini4bRealtimeCorrectionEnabled = target !== 'off';
    row.correctionMode = target;
    this.correctionSavingKeys.add(row.key);
    this.errorMessage = '';
    const sessionRunning = row.sessionActive;

    this.http
      .post<any>(environment.API_Base + '/broadcast/voxtral-set-correction-mode', {
        broadcastKey: row.key,
        mode: target,
      })
      .pipe(finalize(() => this.correctionSavingKeys.delete(row.key)))
      .subscribe({
        next: (resp: any) => {
          const acceptedMode = (resp?.mode as CorrectionMode) || target;
          broadcast.voxtralMini4bRealtimeCorrectionMode = acceptedMode;
          broadcast.voxtralMini4bRealtimeCorrectionEnabled = acceptedMode !== 'off';
          row.correctionMode = acceptedMode;
          if (sessionRunning && resp?.live === false) {
            this.errorMessage = `[${this.shortTitle(row)}] Korrektur-Live-Update fehlgeschlagen — wird beim nächsten Start angewendet.`;
          }
        },
        error: (err: any) => {
          broadcast.voxtralMini4bRealtimeCorrectionMode = previousMode;
          broadcast.voxtralMini4bRealtimeCorrectionEnabled = previousEnabled;
          row.correctionMode = previousMode;
          this.errorMessage =
            err?.error?.message || err?.message || 'Korrektur-Modus konnte nicht aktualisiert werden.';
        },
      });
  }

  toggleContextEditor(row: VoxtralScheduleRow): void {
    if (this.expandedContextKeys.has(row.key)) {
      this.expandedContextKeys.delete(row.key);
    } else {
      this.expandedContextKeys.add(row.key);
    }
  }

  isContextOpen(row: VoxtralScheduleRow): boolean {
    return this.expandedContextKeys.has(row.key);
  }

  /**
   * Two-way binding for the per-row context textarea. We write back to the
   * broadcast object so leaving the editor and reopening it preserves edits
   * even if the user hasn't pressed "Speichern" yet.
   */
  setContext(row: VoxtralScheduleRow, value: string): void {
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    broadcast.voxtralMini4bRealtimeCorrectionContext = value;
  }

  /**
   * Push the edited context back to the backend so the *next* session start
   * uses it. There is no live-update path for the context (rebuilding the
   * corrector mid-stream is out of scope), so this is DDB-only.
   */
  saveContext(row: VoxtralScheduleRow): void {
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    this.persistContextForRow(row, broadcast.voxtralMini4bRealtimeCorrectionContext || '');
  }

  /**
   * Phase 3.3 — `applyToBroadcast` from <app-voxtral-correction-context-editor>.
   * Persists the v2 JSON to the row's broadcast row via the same backend
   * endpoint used by the textarea path. Single source of truth for both flows.
   */
  onCorrectionContextApplied(row: VoxtralScheduleRow, json: string): void {
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (broadcast) {
      broadcast.voxtralMini4bRealtimeCorrectionContext = json;
      row.correctionContext = json;
    }
    this.persistContextForRow(row, json);
  }

  private persistContextForRow(row: VoxtralScheduleRow, context: string): void {
    if (this.correctionSavingKeys.has(row.key)) return;
    this.correctionSavingKeys.add(row.key);
    this.errorMessage = '';
    this.http
      .post(environment.API_Base + '/broadcast/voxtral-correction-context', {
        broadcastKey: row.key,
        correctionContext: context,
      })
      .pipe(finalize(() => this.correctionSavingKeys.delete(row.key)))
      .subscribe({
        next: () => { /* Apollo subscription confirms. */ },
        error: (err: any) => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Korrektur-Kontext konnte nicht gespeichert werden.';
        },
      });
  }

  startVoxtral(row: VoxtralScheduleRow): void {
    if (this.startInFlight.has(row.key)) return;
    const broadcast = this.broadcasts.find((b) => b.key === row.key);
    if (!broadcast) return;
    this.startInFlight.add(row.key);
    this.errorMessage = '';
    // 2026-06-08 — when the broadcast row has no explicit FAB-URL, fall
    // back to the channel-aware default for the current environment
    // (PRD has per-main-channel hosts; regionals always carry ?language=…).
    // Avoid posting null because the scheduler treats null as
    // "use Voxtral:Schedule:DefaultFabUrl" which is empty in dev/stg/prd.
    const fabUrl =
      (broadcast.voxtralMini4bRealtimeFabUrl || '').trim() ||
      defaultFabUrlForChannel(broadcast.channel);
    this.http
      .post(environment.API_Base + '/broadcast/voxtral-start', {
        broadcastKey: row.key,
        fabEnabled: broadcast.voxtralMini4bRealtimeFabEnabled,
        fabSendType: broadcast.voxtralMini4bRealtimeFabMode || 'cursor',
        fabUrl,
        language: 'de',
        correctionContext: broadcast.voxtralMini4bRealtimeCorrectionContext || null,
        correctionEnabled: !!broadcast.voxtralMini4bRealtimeCorrectionEnabled,
      })
      .pipe(finalize(() => this.startInFlight.delete(row.key)))
      .subscribe({
        next: () => { /* Apollo subscription propagates. */ },
        error: (err: any) => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Voxtral konnte nicht gestartet werden.';
        },
      });
  }

  stopVoxtral(row: VoxtralScheduleRow): void {
    if (this.stopInFlight.has(row.key)) return;
    this.stopInFlight.add(row.key);
    this.errorMessage = '';
    this.http
      .post(environment.API_Base + '/broadcast/voxtral-stop', { broadcastKey: row.key })
      .pipe(finalize(() => this.stopInFlight.delete(row.key)))
      .subscribe({
        next: () => { /* Apollo subscription propagates. */ },
        error: (err: any) => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Voxtral konnte nicht gestoppt werden.';
        },
      });
  }

  /** Direct URL prefix to the Voxtral session's REST endpoints. */
  sessionApiBase(row: VoxtralScheduleRow): string | null {
    if (!row.ec2Ip || !row.sessionId) return null;
    return `http://${row.ec2Ip}/api/sessions/${row.sessionId}`;
  }

  /** Short title for error-message prefixes. */
  private shortTitle(row: VoxtralScheduleRow): string {
    const t = row.title || row.key;
    return t.length > 30 ? t.substring(0, 27) + '…' : t;
  }

  isFabSaving(row: VoxtralScheduleRow): boolean {
    return this.fabSavingKeys.has(row.key);
  }

  isCorrectionSaving(row: VoxtralScheduleRow): boolean {
    return this.correctionSavingKeys.has(row.key);
  }

  isStarting(row: VoxtralScheduleRow): boolean {
    return this.startInFlight.has(row.key);
  }

  isStopping(row: VoxtralScheduleRow): boolean {
    return this.stopInFlight.has(row.key);
  }

  close(): void {
    this.closed.emit();
  }

  trackByKey(_index: number, row: VoxtralScheduleRow): string {
    return row.key;
  }

  formatTime(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
  }

  /** Compact HH:mm:ss for the timestamp columns. Empty when value is missing. */
  formatTimeOfDay(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
}
